import type { GetServerSideProps } from 'next';
import type { PageProps } from 'lib/queries/pages';
import type { Page } from 'types/page';
import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import WebPageJson from 'components/structured-data/webpage';
import ProductJson from 'components/structured-data/product';
import PageWrap from 'components/page-wrap';
import Blocks from 'components/blocks';

import { getPageServerSide } from 'lib/queries/pages';
import { parse } from 'cookie';
import { POSTHOG_COOKIE_KEY } from 'lib/cookies/posthog-cookie';
import { default as posthogAPI } from 'lib/posthog';
import camelcaseKeys from 'camelcase-keys';
import { usePostHog } from 'posthog-js/react';
import StickyMobileCta from 'components/sticky-mobile-cta';
import MedicalReviewer from 'components/medical-reviewer';
import MedicalWebPageJson from 'components/structured-data/medical-webpage';
import ServiceJson from 'components/structured-data/service';
import { getPostHogServerClient } from 'lib/posthog-server';

const HeroCard = dynamic(() => import('components/hero-card'));
const HeroCardNew = dynamic(() => import('components/hero-card-new'));
const Loader = dynamic(() => import('components/loader'));

const staticPage = [
  {
    id: 83930,
    date: '2024-11-18T11:01:27',
    modified: '2026-01-19T02:22:27',
    slug: 'california',
    title: 'Get a California (CA) Medical Marijuana Card Online',
    content: '',
    acf: {
      city: '',
      page_type: 'card',
      has_referral: true,
      hero: {
        title: 'Get a California Medical Marijuana Card',
        rich_title: '',
        renew_title: '',
        sub_title: 'Get Approved in Minutes',
        form_id: '',
        form_type: 'partner',
        form_title: '',
        form_label: '',
        renew_form_content_items: false,
        form_submit_text: 'Get Started',
        form_submit_url: '',
        address: '',
        description:
          '<p>See a CA-licensed medical marijuana doctor online in minutes and get the cannabis care you deserve.</p>\n',
        renew_description: '',
        image: null,
        image_alignment: 'right',
        steps: false,
        button: null,
        download_button: null,
        badges: [
          {
            label: '',
            icon: null,
          },
          {
            label: '',
            icon: null,
          },
          {
            label: '',
            icon: null,
          },
        ],
        google_my_business_details: {
          medical_card_utm: '',
          cannabis_counseling_services_utm: '',
          phone_number: '',
        },
        webinar_members: false,
        quick_links: [
          {
            section: 'qualifying-conditions',
            title: 'Do I qualify?',
            description: 'Check the qualifying conditions within seconds.',
            icon: {
              id: 95920,
              src: 'https://admin.leafwell.com/app/uploads/2025/11/icon-qualify.svg',
              width: 1,
              height: 1,
              srcSet: false,
              alt: '',
            },
          },
          {
            section: 'cost-information',
            title: 'Affordable price',
            description: 'Risk-free process. Get approved or your money back.',
            icon: {
              id: 95921,
              src: 'https://admin.leafwell.com/app/uploads/2025/11/icon-affordable.svg',
              width: 1,
              height: 1,
              srcSet: false,
              alt: '',
            },
          },
          {
            section: 'quick-steps',
            title: 'Quick, easy process',
            description:
              'Book an appointment, consult with a doctor, and receive your certification in just a few minutes.',
            icon: {
              id: 95922,
              src: 'https://admin.leafwell.com/app/uploads/2025/11/icon-quick-easy.svg',
              width: 1,
              height: 1,
              srcSet: false,
              alt: '',
            },
          },
        ],
        latitude: '',
        longitude: '',
        opening_hours: false,
      },
      linktree: {
        categories: false,
      },
      redirect_medical_card: true,
      redirect_product_endpoint: '',
      custom_state_code: '',
      pause_appointments: false,
      is_affiliate: false,
      affiliate_logo: null,
      affiliate_website: '',
      form_url: '',
      vpd_page: [],
      is_experiment: false,
      is_renewal: false,
      is_clinic: false,
      total_medical_card_price: '35',
      name: 'Get a California (CA) Medical Marijuana Card Online',
      description:
        'Get your CA medical marijuana card online for just $35. Connect with a Leafwell healthcare provider now and get certified fast!',
      image_object_caption:
        'California (CA) Medical Marijuana Card by Leafwell',
      product: {
        name: 'California Medical Marijuana Card',
        description:
          'A California medical marijuana card from Leafwell allows you to purchase cannabis legally from dispensaries throughout CA.',
        image_url: {
          id: 74472,
          src: 'https://admin.leafwell.com/app/uploads/2024/01/CA.jpg',
          width: 420,
          height: 428,
          srcSet:
            'https://admin.leafwell.com/app/uploads/2024/01/CA.jpg 420w, https://admin.leafwell.com/app/uploads/2024/01/CA-393x400.jpg 393w',
          alt: 'California Medical Cannabis Card',
        },
      },
      state_product_image: null,
      is_vpd_landing: false,
      vpd_logo: null,
      price: {
        price: '',
        new_price: '',
        percentage_discount: '',
        discount_code: '',
        free_discount_code: '',
      },
      patient_renewal_dropdown: false,
      patient_renewal_dropdown_info:
        'Prior to your consultation, we will verify your status within the program',
      affiliate_page: [],
      webinar_info: {
        webinar_title: '',
        webinar_description: '',
        webinar_zoom_url: '',
      },
      vpd_partner_name: false,
      states: ['CA'],
      blocks: [
        {
          acf_fc_layout: 'benefits_of_mmc',
          instance_id: '0aa76c73',
          section_id: 'key-benefits-of-mmc',
          title: 'The key benefits of a medical marijuana card in California',
          access_to_dispensaries: {
            title: 'Access to dispensaries',
            description:
              '<p>Some local jurisdictions in California have restricted recreational access, so those with an MMIC have access to medical-only dispensaries in places with such restrictions.</p>\n',
          },
          tax_savings: {
            title: 'Save <strong>up to 10.25%</strong> on products',
            description:
              '<p>Save up to 10.25% in state taxes compared to recreational users. <strong>Save up to $324</strong> per year based on $150 monthly spend.</p>\n',
          },
          cultivation: {
            title: 'Cultivation',
            description:
              '<p>Medical and recreational cannabis users can <strong>grow up to 6 plants</strong>. Medical card holders (adults 21+) can obtain a grower&#8217;s certification which allows them to grow up to 99 plants.</p>\n',
          },
          higher_possession: {
            title: 'Higher possession',
            description:
              '<p>There is no defined possession limit for medical patients.</p>\n<p>Recreational users can possess 28.5 g of cannabis.</p>\n',
          },
          access_to_products: {
            title: 'Access <strong>higher-potency</strong> cannabis products',
            description:
              '<p>Medical patients may have access to higher-potency cannabis products (compared to recreational users).</p>\n',
          },
          lower_age_restriction: {
            title:
              'Get legal cannabis <strong>at age 18+</strong>, rather than 21+',
            description:
              '<p>Patients 18+ can qualify for a medical card.</p>\n<p>Minors can also apply for a medical card if they have an approved caregiver.</p>\n<p>Recreational dispensaries are available to residents aged 21+.</p>\n',
          },
          legal_protection: {
            title: 'Enjoy <strong>legal protections</strong> for employment',
            description: '',
          },
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '4e911875',
          container_size: 'default',
          content:
            '<div class="mx-auto max-w-2xl"><div data-tax-savings=true data-initial-state="CA"></div></div>\n',
          section_id: '',
        },
        {
          acf_fc_layout: 'reviews',
          instance_id: '94149289',
          section_id: 'testimonials',
          title: 'Trusted by 1 Million Monthly Users',
          intro: '',
          trustpilot: true,
          show_trust_signals: true,
          variant: 'full',
          quoted: false,
        },
        {
          acf_fc_layout: 'cards_image',
          instance_id: '111f6671',
          section_id: 'quick-steps',
          title: '4 easy steps to get your CA medical card',
          label: 'Get approved in minutes using Leafwell’s online platform.',
          label_position: 'below_title',
          list_numbered: true,
          cards_background_color: 'beige',
          cards: [
            {
              title: 'Sign up',
              description:
                '<p>Register with Leafwell on any device and consult with a licensed medical marijuana doctor in minutes.</p>\n',
              image: {
                id: 96266,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-signup-yellow.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: 'sign up icon',
              },
            },
            {
              title: 'Get your e-certification',
              description:
                '<p>We’ll immediately email your digital certificate after your approval.</p>\n',
              image: {
                id: 96267,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-certificate-purple.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: 'certificate',
              },
            },
            {
              title: 'Get your certification',
              description:
                '<p>Receive your paper certificate and optional plastic medical card via mail within 5 days.</p>\n',
              image: {
                id: 96267,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-certificate-purple.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: 'certificate',
              },
            },
            {
              title: '(Optional): Get a state card',
              description:
                '<p>Fill out your application with the California Department of Public Health and pay your non-refundable application fee.</p>\n',
              image: {
                id: 96269,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-get-card-pink.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: 'card',
              },
            },
          ],
          description: '',
          button: '',
        },
        {
          acf_fc_layout: 'youtube_video',
          instance_id: 'f6182b18',
          section_id: '',
          title: 'A few more questions about the process?',
          content:
            'Watch this video to learn more about getting a card in the California medical marijuana program.\r\n',
          variant: 'none',
          video_size: 'small',
          youtube_videos: [
            {
              youtube_video_id: 'qnGJ_tH4nzQ',
              image: null,
              play_button_style: 'secondary',
            },
          ],
        },
        {
          acf_fc_layout: 'table',
          instance_id: '5f667c76',
          section_id: 'cost-information',
          display_product_state_image: false,
          title: 'How much does the CA card cost?',
          title_tag: 'h3',
          intro_text:
            '<p>Qualified patients in California save more with Leafwell. Get your money back if not approved.</p>\n',
          table: {
            columns: [
              {
                column_type: 'values',
                label: '',
                values: [
                  {
                    value_text: 'Medical marijuana certificate',
                    value_check: false,
                  },
                  {
                    value_text: 'State application fee (optional)',
                    value_check: false,
                  },
                  {
                    value_text: 'Total',
                    value_check: false,
                  },
                ],
              },
              {
                column_type: 'values',
                label: 'New with Leafwell',
                values: [
                  {
                    value_text: '$35',
                    value_check: false,
                  },
                  {
                    value_text: '$0-$100',
                    value_check: false,
                  },
                  {
                    value_text: '$35-$135',
                    value_check: false,
                  },
                ],
              },
              {
                column_type: 'values',
                label: 'Renewal with Leafwell',
                values: [
                  {
                    value_text: '$35',
                    value_check: false,
                  },
                  {
                    value_text: '$0-$100',
                    value_check: false,
                  },
                  {
                    value_text: '$35-$135',
                    value_check: false,
                  },
                ],
              },
            ],
          },
          button: {
            title: 'Apply Now',
            url: '#leafwell-signup-form',
            target: '',
          },
        },
        {
          acf_fc_layout: 'content_and_banner',
          instance_id: '625fdb6c',
          section_id: 'qualifying-conditions',
          title: 'How to qualify for the California medical marijuana program',
          background_color: 'beige',
          content_left:
            '<p>To apply to the California medical marijuana program, you must be a legal resident of the state. Patients must be aged 18 or over to apply for a medical marijuana card, and minors can have caregivers apply on their behalf. Currently, California law states that those with the<a href="https://cannabis.ca.gov/consumers/medicinal-cannabis"> following conditions</a> can receive a medical card:</p>\n<ul>\n<li><a href="https://leafwell.com/medical-conditions/hiv-aids">AIDS</a></li>\n<li><a href="https://leafwell.com/medical-conditions/eating-disorders">Anorexia</a></li>\n<li><a href="https://leafwell.com/medical-conditions/arthritis">Arthritis</a></li>\n<li><a href="https://leafwell.com/medical-conditions/cachexia-or-wasting-syndrome">Cachexia (Wasting Syndrome)</a></li>\n</ul>\n',
          content_right:
            '<ul>\n<li><a href="https://leafwell.com/medical-conditions/cancer">Cancer</a></li>\n<li><a href="https://leafwell.com/medical-conditions/chronic-pain-muscle-pain">Chronic pain</a></li>\n<li><a href="https://leafwell.com/medical-conditions/glaucoma">Glaucoma</a></li>\n<li><a href="https://leafwell.com/medical-conditions/migraine-headache">Migraine</a></li>\n<li>Persistent <a href="https://leafwell.com/medical-conditions/muscle-spasms">muscle spasms</a> (e.g., spasms associated with <a href="https://leafwell.com/medical-conditions/multiple-sclerosis">multiple sclerosis</a>)</li>\n<li><a href="https://leafwell.com/medical-conditions/seizures">Seizures</a> (e.g., <a href="https://leafwell.com/medical-conditions/epilepsy">epileptic seizures</a>)</li>\n<li><a href="https://leafwell.com/medical-conditions/nausea-mitigation">Severe nausea</a></li>\n<li aria-level="1">Additionally, physicians may recommend cannabis to qualified medical marijuana patients with a condition or symptoms that limit their ability to conduct major life activities or for symptoms that can cause serious harm to their physical or mental health and safety.</li>\n</ul>\n',
          banner_title: 'Convenient, Quality Care When You Need It Most',
          banner_label:
            "With Leafwell, you'll be matched up with a highly knowledgeable, empathetic, licensed medical marijuana doctor or provider.",
          banner_image: {
            id: 76039,
            src: 'https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors.png',
            width: 1568,
            height: 750,
            srcSet:
              'https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-700x335.png 700w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-1024x490.png 1024w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-400x191.png 400w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-768x367.png 768w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-1536x735.png 1536w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors.png 1568w',
            alt: 'health care providers',
          },
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: 'ac280a2e',
          container_size: 'default',
          content:
            '<h2><b>2 documents needed to apply for the California medical marijuana identification card program</b></h2>\n<p>To apply for a medical marijuana card in California, you must follow a few essential steps to ensure your eligibility and successful registration. The process begins by gathering the necessary documents and information, securing a medical marijuana recommendation, and providing proof of identity.</p>\n<ol>\n<li><strong>Proof of Identity</strong><br />\nDriver’s license, state ID, passport, or government-issued photo ID card.</li>\n<li><strong>Medical Marijuana Recommendation</strong><br />\nConsult with a registered MMJ physician who can evaluate your condition and provide a recommendation. It is essential to choose a physician who is experienced in treating patients with your specific condition, as they will be able to provide the necessary certification that forms a critical part of your application.</li>\n</ol>\n<div data-highlight="button" data-label="Get My Card" data-ref="#leafwell-signup-form" data-target="_self"></div>\n',
          section_id: '',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '3670c026',
          container_size: 'default',
          content:
            '<h2>Next steps after approval</h2>\n<h3>Using Your California Medical Marijuana Recommendation</h3>\n<ol>\n<li aria-level="">Download the pdf of your doctor’s recommendation letter from your Leafwell patient dashboard.</li>\n<li aria-level="">Save it to your device.</li>\n<li aria-level="">You can start using this electronic copy immediately at a licensed medical dispensary*. You will receive a hard copy of your recommendation letter and, if you chose our $59 plan, a physician-issued Leafwell patient ID card in the mail within 7-10 business days.<em>*Please note that a recreational dispensary may not accept the electronic recommendation and may require a hard copy letter.</em></li>\n<li aria-level="">Once you receive your hard copy recommendation, you can then legally purchase medical marijuana from any licensed dispensary. To purchase medical marijuana you will need to bring a hard copy of your Leafwell doctor’s recommendation letter or your Leafwell patient ID card, and a valid form of photo identification, such as a driver’s license, with you to the dispensary.</li>\n</ol>\n<h3>OPTIONAL: Register for the <a href="https://www.cdph.ca.gov/CDPH%20Document%20Library/ControlledForms/cdph9042.pdf" target="" rel="">CA MMIC program</a> at your local county office.</h3>\n<p>The state registration is completely optional. This allows you to obtain a state-issued card.</p>\n<h4>Why register?</h4>\n<ul>\n<li aria-level="">You will not have to pay sales and use tax when making retail medical marijuana purchases.</li>\n<li aria-level="">A Medical Marijuana Identification Card (MMIC) identifies you as protected under Proposition 215 and Senate Bill 94.</li>\n<li aria-level="">An MMIC helps law enforcement identify you as legally allowed to possess certain amounts of cannabis under specific conditions.</li>\n</ul>\n<h4>Registration Fees</h4>\n<ul>\n<li aria-level="">These vary county to county but the maximum you can be charged is $100.</li>\n<li aria-level="">A 50% fee reduction is issued per card if you are eligible for Medi-Cal.</li>\n<li aria-level="">Fees are waived for indigent patients who are eligible for and participate in, the Country Medical Services Program.</li>\n</ul>\n<h4>Registration Process</h4>\n<ol>\n<li aria-level="">This process requires an in-person application at your county office.</li>\n<li aria-level="">You will receive an official state-issued card, and any dispensary or law enforcement agency can verify your patient number online immediately.</li>\n<li aria-level="">It is your option to designate a primary caregiver and apply for their identification card at the time you submit your application.</li>\n<li aria-level="">You will need to provide your certifying physician&#8217;s information (supplied in the PDF emailed to you after the appointment) as well as which medical board certified them:\n<ol>\n<li>Dr. Abdulla &#8211; Medical Board of California</li>\n<li>Dr. Berman &#8211; Medical Board of California</li>\n<li>Dr. Chang &#8211; Medical Board of California</li>\n<li>Dr. Chen &#8211; Medical Board of California</li>\n<li>Dr. Epstein &#8211; Osteopathic Medical Board of California</li>\n<li>Dr. Miller &#8211; Medical Board of California</li>\n<li>Dr. Moreland &#8211; Medical Board of California</li>\n<li>Dr. Steinhardt &#8211; Medical Board of California</li>\n</ol>\n</li>\n</ol>\n',
          section_id: 'state-steps',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '9aa1b962',
          container_size: 'default',
          content:
            '<h2>How to renew your medical marijuana ID card</h2>\n<p>There is no difference between getting a recommendation letter as an existing cardholder or as a new medical cannabis patient.</p>\n<p>Here is the process for getting your renewal letter:</p>\n<ol>\n<li>Sign in to your Leafwell account or register as a renewal patient.</li>\n<li>Speak to a California-licensed physician online.</li>\n<li>Get approved and download an electronic version of your recommendation letter.</li>\n<li>Purchase cannabis from a licensed medical dispensary. You will receive a hard copy of your recommendation in the mail within 3-5 business days.</li>\n</ol>\n',
          section_id: 'renew',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '66c5bd6b',
          container_size: 'default',
          content:
            '<h2>Legal limitations for California cardholders</h2>\n<ul>\n<li>Medical patients cannot exceed the equivalent possession amounts if carrying both concentrate and flower. This means you cannot have both an ounce of dried cannabis flower and eight grams of concentrate simultaneously.</li>\n<li aria-level="1">No smoking cannabis where smoking tobacco is not permitted.</li>\n<li aria-level="1">No smoking cannabis in public unless allowed by public ordinance; doing so could result in a fine, especially if you are near a government building, public park, main street, or anywhere where there might be large congregations of children.</li>\n<li aria-level="1">Using cannabis while driving is illegal. You cannot legally drive when under the influence of cannabis, and any cannabis kept in the car with you must be kept locked away and out of sight. Passengers in the vehicle are not legally allowed to consume cannabis while in a vehicle.</li>\n<li aria-level="1">You cannot ingest cannabis within 1,000 feet of a school, daycare center, or youth center unless you are in a private residence and any cannabis smell or smoke is not detectable by children.</li>\n<li aria-level="1">Any cannabis must be kept out of reach of children. Treat it as you would any other medication and lock it away. Your<a href="https://leafwell.com/blog/can-you-own-a-gun-with-a-medical-marijuana-card"> Second Amendment right to bear arms</a> is also affected.</li>\n<li aria-level="1">It&#8217;s legal to get certified for medical cannabis through telemedicine. This means that you can use services like Leafwell to start the process of obtaining an MMJ card.</li>\n<li aria-level="1">Hemp-derived <a href="https://leafwell.com/blog/what-is-cbd">CBD</a> products are legal. <a href="https://leafwell.com/blog/delta-8-thc">Delta-8</a> and other synthetic or semi-synthetic cannabinoids are legal for individuals over 21 in California.</li>\n</ul>\n',
          section_id: 'laws',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '4a1aa311',
          container_size: 'default',
          content:
            '<h2>California medical marijuana program caregiver rules</h2>\n<p>Medical cannabis caregivers must be at least 18 and meet the following criteria to apply:</p>\n<ul>\n<li aria-level="1">The person you care for is legally entitled to use medical marijuana.</li>\n<li aria-level="1">You meet the legal definition of a primary caregiver under the Compassionate Use Act. &#8220;Primary caregiver&#8221; means the individual designated by the person exempted under this section who has consistently assumed responsibility for the housing, health, or safety of that person.</li>\n</ul>\n<p>Primary caregivers may possess and cultivate an amount of marijuana reasonably related to the patient&#8217;s current medical needs. A caregiver may apply for a Medical Marijuana Identification Card (MMIC) but does not need to do this, just as patients don&#8217;t need a card.</p>\n',
          section_id: 'caregiver',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '97b7db2a',
          container_size: 'default',
          content:
            '<h2>Can you use a California medical marijuana ID card in other states?</h2>\n<p>California does not have medical marijuana reciprocity. Thankfully, since the state does allow for adult-use cannabis sales, you do not need your medical card from another state to buy cannabis in California as long as you are over 21.</p>\n<p>Many other states and territories, however, have medical marijuana reciprocity and accept out-of-state cards. However, most states require a state-issued card like the MMIC, so a regular California recommendation may not be accepted for reciprocity in the following states:</p>\n<ul>\n<li>Arizona</li>\n<li>Arkansas *</li>\n<li>Delaware</li>\n<li>Hawaii *</li>\n<li>Louisiana</li>\n<li>Maine</li>\n<li>Michigan (at the dispensary&#8217;s discretion)</li>\n<li>Nevada</li>\n<li>New Hampshire</li>\n<li>New Jersey *</li>\n<li>New Mexico</li>\n<li>Oklahoma *</li>\n<li>Puerto Rico</li>\n<li>Rhode Island</li>\n<li>U.S. Virgin Islands *</li>\n<li>Utah * (up to two 21-day periods in a calendar year)</li>\n<li>Washington, D.C.</li>\n</ul>\n<p>*Visitors must complete a visiting patient application with the state program.</p>\n',
          section_id: 'reciprocity',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '516ba121',
          container_size: 'default',
          content:
            '<h2>Minor patient information</h2>\n<p>Minors under 18 can have caregivers apply on their behalf. Like adult patients, minors must obtain a qualified healthcare provider’s recommendation.</p>\n<p>During the application process, minors must designate a caregiver, who must submit their own application and identifying materials.</p>\n',
          section_id: 'minor',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '7467df72',
          container_size: 'default',
          content:
            '<h2>May I purchase medical marijuana with only my doctor&#8217;s recommendation letter, or do I need the State of California&#8217;s Medical Marijuana Identification Card?</h2>\n<p>Yes, you can purchase medical marijuana in California (and in some other states) with only your doctor&#8217;s recommendation letter.</p>\n<p>While some states permit California patients to purchase medical marijuana while visiting, most dispensaries will require you to have the official California Medical Marijuana Identification Card and will not accept your doctor&#8217;s recommendation letter. Click <a href="https://www.cdph.ca.gov/Programs/CHSI/Pages/MMICP.aspx" target="" rel="">here</a> for more information on applying for the California Medical Marijuana Identification Card.</p>\n',
          section_id: 'recommendation',
        },
        {
          acf_fc_layout: 'faqs',
          instance_id: 'a8e610b2',
          section_id: 'faqs',
          title: 'FAQs',
          description:
            '<p>Discover answers to frequently asked questions about medical cannabis in California.</p>\n',
          items: [
            {
              id: 23185,
              title: 'How do I apply to the state program in California?',
              content:
                '<strong>Using Your California Medical Marijuana Recommendation</strong>\r\n<ol>\r\n \t<li aria-level="1">Download the pdf of your Doctor\'s recommendation letter from your Leafwell patient dashboard.</li>\r\n \t<li aria-level="1">Save it to your device.</li>\r\n \t<li aria-level="1">You can start using this electronic copy immediately at a licensed medical dispensary.*</li>\r\n \t<li aria-level="1">You will receive a hard copy of your recommendation letter and, if you chose our $59 plan, a physician-issued Leafwell patient ID card in the mail within 3-5 business days.</li>\r\n \t<li aria-level="1">Once you receive your hard copy recommendation, you can then legally purchase medical marijuana from any licensed dispensary. To purchase medical marijuana you will need to bring a hard copy of your Leafwell doctor\'s recommendation letter or your Leafwell patient ID card, and a valid form of photo identification, such as a driver\'s license, with you to the dispensary.</li>\r\n</ol>\r\n*Please note that a recreational dispensary may not accept the electronic recommendation and may require a hard copy letter.\r\n<b>OPTIONAL: Register for the CA MMIC program at your local county office.</b>\r\n\r\nThe state registration is completely optional. This allows you to obtain a state-issued card.\r\n\r\n<b>Why register?</b>\r\n<ul>\r\n \t<li aria-level="1">You will not have to pay sales and use tax when making retail medical marijuana purchases.</li>\r\n \t<li aria-level="1">A Medical Marijuana Identification Card (MMIC) identifies you as protected under Proposition 215 and Senate Bill 94.</li>\r\n \t<li aria-level="1">An MMIC helps law enforcement identify you as legally allowed to possess certain amounts of cannabis under specific conditions.</li>\r\n</ul>\r\n<h4><b>Registration Fees</b></h4>\r\n<ul>\r\n \t<li aria-level="1">These vary county to county but the maximum you can be charged is $100.</li>\r\n \t<li aria-level="1">A 50% fee reduction is issued per card if you are eligible for Medi-Cal.</li>\r\n \t<li aria-level="1">Fees are waived for indigent patients who are eligible for and participate in, the Country Medical Services Program.</li>\r\n</ul>\r\n<h4><b>Registration Process</b></h4>\r\n<ol>\r\n \t<li aria-level="1">This process requires an in-person application at your county office.</li>\r\n \t<li aria-level="1">You will receive an official state-issued card, and any dispensary or law enforcement agency can verify your patient number online immediately.</li>\r\n \t<li aria-level="1">It is your option to designate a primary caregiver and apply for their identification card at the time you submit your application.</li>\r\n</ol>',
            },
            {
              id: 81946,
              title:
                'How much medical marijuana am I allowed to possess in California with a medical marijuana recommendation?',
              content:
                "In California, medical marijuana patients can possess up to <strong>8 ounces</strong> or up to 60 grams of cannabis concentrate (1 oz. being equivalent to 8 grams) on their person.\r\n\r\nPatients or their caregivers can grow up to 6 mature plants or 12 immature plants, or a larger amount consistent with the patient's reasonable needs, not exceeding 99 plants. To grow more than 6 mature or 12 immature plants, patients will need to get a grower's recommendation in addition to their Leafwell doctor's recommendation.\r\n\r\nRecreational users can possess up to 28.5 grams of flower, 8 grams of concentrate, and 6 plants.",
            },
            {
              id: 81948,
              title:
                'How often do I need to renew my California medical marijuana card?',
              content:
                'California medical marijuana recommendations are <strong>valid for one year</strong> and can be renewed online. We recommend renewing 5-7 business days before your expiration date to avoid a lapse in coverage and limit any lost time on your current recommendation.',
            },
            {
              id: 81950,
              title:
                "May I purchase medical marijuana with only my doctor's recommendation letter?",
              content:
                'Yes, you can purchase medical marijuana in California (and in some other states) with only your doctor\'s recommendation letter.\r\n\r\nWhile some states permit California patients to purchase medical marijuana while visiting, most dispensaries will require you to have the official California Medical Marijuana Identification Card and will not accept your doctor\'s recommendation letter. Click <a href="https://www.cdph.ca.gov/Programs/CHSI/Pages/MMICP.aspx">here</a> for more information on applying for the California Medical Marijuana Identification Card.',
            },
            {
              id: 81952,
              title: 'What are the medical marijuana laws in California?',
              content:
                '<strong>1972</strong> – California became the first state to vote on a ballot which argued for the legalization of cannabis. Proposition 19 would have legalized use, possession and cultivation of cannabis (but not commercial sales). It lost 33 percent to 67 percent but set California up as a state which would lead the way.\r\n\r\n<strong>1996</strong> – Medical marijuana law comes into effect – Proposition 215 refers to the Compassionate Use Act of 1996 which is still in effect today. This passage exempts patients and defined caregivers who possess or cultivate cannabis for medical treatment and who have a physician’s recommendation from punishment under state law. California was the first state to legalize medical cannabis.\r\n\r\n<strong>2003</strong> – Senate Bill 420 clarified much of the language in the initial law and established the current identification-card system for medical cannabis patients.\r\n\r\n<strong>2016</strong> – Proposition 64, the Adult Use of Marijuana Act, legalized the recreational use of cannabis. It passed with 57 percent of the vote. Recreational cannabis enthusiasts can possess up to one ounce of cannabis flower or 8 grams of concentrate (e.g. hashish, wax, shatter).\r\n\r\n<strong>2019</strong> – Amendments were passed via Senate Bill 34 which allowes licensed retailers and dispensaries to provide medical cannabis for free to qualified patients who are for certain reasons unable to access product.\r\n\r\n<strong>2020</strong> – Governor Newsom issued an executive order which extended existing MMICs beyond the original expiration date due to Covid-19 and a lack of access to some facilities',
            },
          ],
          button: '',
        },
        {
          acf_fc_layout: 'table_of_contents',
          instance_id: '7d7470e4',
          table_of_contents: [
            {
              label: 'Testimonials',
              id: 'testimonials',
            },
            {
              label: 'Quick Steps',
              id: 'quick-steps',
            },
            {
              label: 'Benefits of Getting a Card',
              id: 'benefits-card',
            },
            {
              label: 'State-Specific Steps',
              id: 'state-specific-steps',
            },
            {
              label: 'Cost Information',
              id: 'cost-information',
            },
            {
              label: 'Qualifying Conditions',
              id: 'qualifying-conditions',
            },
            {
              label: 'What You Need to Know',
              id: 'what-you-need-to-know',
            },
            {
              label: 'What You Need',
              id: 'what-you-need',
            },
            {
              label: 'More Questions',
              id: 'more-questions',
            },
            {
              label: 'Next Steps After Approval',
              id: 'state-steps',
            },
            {
              label: 'How to Renew Your Card',
              id: 'renew',
            },
            {
              label: 'Legal topics',
              id: 'laws',
            },
            {
              label: 'Caregivers',
              id: 'caregiver',
            },
            {
              label: 'Reciprocity',
              id: 'reciprocity',
            },
            {
              label: 'Minor patient information',
              id: 'minor',
            },
            {
              label:
                "May I purchase medical marijuana with only my doctor's recommendation letter, or do I need the State of California's Medical Marijuana Identification Card?",
              id: 'recommendation',
            },
            {
              label: 'FAQs',
              id: 'faqs',
            },
          ],
        },
      ],
    },
    seo: {
      title: 'How to get a medical marijuana card in California - Leafwell',
      description:
        'Get or renew your CA medical marijuana card with Leafwell. Our easy process will help you get access to medical cannabis in California fast.',
      canonical: 'https://leafwell.com/medical-card/california',
      images: [
        {
          source_url:
            'https://admin.leafwell.com/app/uploads/2022/01/share-1024x538.jpg',
          alt_text: '',
          media_details: {
            width: 1024,
            height: 538,
          },
        },
      ],
      meta_robots_noarchive: false,
      meta_robots_nofollow: false,
      meta_robots_noindex: false,
      opengraph: {
        title: 'How to get a medical marijuana card in California - Leafwell',
        description:
          'Get or renew your CA medical marijuana card with Leafwell. Our easy process will help you get access to medical cannabis in California fast.',
        type: 'website',
      },
      redirect: null,
    },
    language: 'en',
    translations: [
      {
        uri: '/medical-card/california',
        slug: 'en',
        locale: 'en',
      },
    ],
  },
];

const PageContent: React.FC<PageProps> = ({
  page: {
    title,
    seo,
    date,
    modified,
    slug,
    acf: {
      hero,
      name,
      affiliateLogo,
      affiliateWebsite,
      formUrl,
      pauseAppointments,
      redirectMedicalCard,
      redirectProductEndpoint,
      redirectCannabisCounseling,
      description,
      imageObjectCaption,
      blocks,
      pageType,
      stateProductImage,
      product,
      totalMedicalCardPrice,
      states,
      city,
      hasReferral,
      isClinic,
    },
  },
  settings: { seoSettings },
  variant,
  featureFlag,
  inPersonEvents,
}) => {
  const posthog = usePostHog();

  useEffect(() => {
    if (featureFlag) {
      /*       posthog.capture('$feature_flag_called', {
        $feature_flag: featureFlag,
        $feature_flag_response: variant,
      }); */
      posthog.capture('$pageview', {
        experiment_page: true,
      });
    }
  }, [featureFlag, variant]);

  return (
    <PageWrap seo={seo} pageType={pageType} title={title}>
      <MedicalWebPageJson
        seo={seo}
        date={date}
        title={title}
        modified={modified}
        stateName={slug.replace(/-/g, ' ').replace(/\b\w/g, function (letter) {
          return letter.toUpperCase();
        })}
        siteUrl={seoSettings.siteUrl}
        name={name}
        description={description}
        imageObjectCaption={imageObjectCaption}
        siteDescription={seoSettings.homepage.homepageDescription}
      />
      <ServiceJson
        seo={seo}
        product={product}
        totalMedicalCardPrice={totalMedicalCardPrice}
        stateName={slug.replace(/-/g, ' ').replace(/\b\w/g, function (letter) {
          return letter.toUpperCase();
        })}
      />
      {variant === 'test' ? (
        <>
          <HeroCardNew
            hero={hero}
            states={states}
            affiliateLogo={affiliateLogo}
            pauseAppointments={pauseAppointments}
            redirectMedicalCard={redirectMedicalCard}
            inPersonEvents={inPersonEvents}
            showDoIQualify={false}
            addEmailInput={false}
            passwordless={true}
          />
          <StickyMobileCta
            observeTargetId="hero-cta-sentinel"
            stateCode={states?.[0]}
            formSubmitText={hero.formSubmitText || 'Get your card'}
          />
        </>
      ) : (
        <>
          <HeroCard
            hero={hero}
            pageType={pageType}
            states={states}
            formUrl={formUrl}
            affiliateLogo={affiliateLogo}
            affiliateWebsite={affiliateWebsite}
            pauseAppointments={pauseAppointments}
            redirectMedicalCard={redirectMedicalCard}
            redirectCannabisCounseling={redirectCannabisCounseling}
            totalMedicalCardPrice={totalMedicalCardPrice}
            city={city}
            isClinic={isClinic}
            redirectProductEndpoint={redirectProductEndpoint}
          />
          <StickyMobileCta
            observeTargetId="leafwell-signup-form"
            stateCode={states?.[0]}
            formSubmitText={hero.formSubmitText || 'Get your card'}
          />
        </>
      )}
      {blocks && blocks.length > 0 ? (
        <Blocks
          blocks={blocks}
          pageType={pageType}
          states={states}
          modified={modified}
          stateProductImage={stateProductImage}
          hasReferral={hasReferral}
          variant={variant}
        />
      ) : null}
      <MedicalReviewer modified={modified} />
    </PageWrap>
  );
};

const Page: React.FC<PageProps> = props => {
  const { isFallback } = useRouter();

  if (isFallback) {
    return <Loader />;
  }

  return <PageContent {...props} />;
};

export const getServerSideProps = (async context => {
  const { req } = context;

  const distinctId = req.headers['x-posthog-distinct-id'] as string;
  const flagKey = 'website-new-design-passwordless-CA';

  let variant: string | null = null;

  try {
    const posthog = getPostHogServerClient();
    const flags = await posthog.evaluateFlags(distinctId, {
      personProperties: { $pathname: '/medical-card/california' },
    });

    const flagValue = flags.getFlag(flagKey) as string | undefined;

    if (flagValue) {
      variant = flagValue;
    } else {
      console.warn(
        `[PostHog] Flag "${flagKey}" returned undefined for ${distinctId} — excluding from experiment`,
      );
    }
  } catch (error) {
    console.warn(
      `[PostHog] evaluateFlags failed for ${distinctId}:`,
      error?.message || error,
      '— excluding from experiment',
    );
  }

  return await getPageServerSide({
    ...context,
    variant: variant as 'test' | 'control' | null,
    featureFlag: variant ? flagKey : null,
    slug: 'medical-card/california',
    staticPage: camelcaseKeys(staticPage[0], { deep: true }),
    posthogBootstrap: {
      distinctID: distinctId,
      featureFlags: variant ? { [flagKey]: variant } : {},
    },
  });
}) satisfies GetServerSideProps<PageProps>;

export default Page;
